/**
 * Uses `geoAxios` — a dedicated instance, deliberately not the shared
 * `axiosInstance`/`ApiClient` — see `geoAxios.ts` for why.
 */
import { createRateLimiter } from '@/common/utils/createRateLimiter';
import { GEOLOCATION_CONFIG } from '@/features/geolocation/config';
import { geoAxios } from './geoAxios';
import { nominatimSearchResponseSchema, type NominatimPlace } from './schemas';

/**
 * One limiter for the whole Nominatim host. OSM policy allows max 1 request per
 * second; the tier chain therefore queues instead of bursting.
 */
const nominatimLimiter = createRateLimiter(() => GEOLOCATION_CONFIG.nominatim.minIntervalMs);

export const NominatimService = {
  /**
   * Structured search. `query` must contain only structured params
   * (`street` / `city` / `postalcode` / `country`) — newer Nominatim errors out
   * when they are combined with a free-form `q`.
   */
  search: async (
    query: Record<string, string>,
    language: string,
    signal?: AbortSignal
  ): Promise<NominatimPlace[]> => {
    const params = new URLSearchParams({
      ...query,
      format: 'jsonv2',
      addressdetails: '1',
      limit: String(GEOLOCATION_CONFIG.nominatim.limit),
      'accept-language': language,
    });

    if (query.country) {
      params.set('countrycodes', query.country.toLowerCase());
    }

    const url = `${GEOLOCATION_CONFIG.nominatim.baseUrl}/search?${params.toString()}`;
    const response = await nominatimLimiter.schedule(
      () => geoAxios.get(url, { signal, headers: { Accept: 'application/json' } }),
      signal
    );

    return nominatimSearchResponseSchema.parse(response.data);
  },
};
